import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Edit2, Printer, Truck, FileText, User, Info, Clock, CheckCircle } from 'lucide-react';
import PartsConfigTable from './components/PartsConfigTable.js';

const PCDetail = ({ db, user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pcData, setPcData] = useState(null);
  const [firms, setFirms] = useState([]);
  const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        const firmsList = firmSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFirms(firmsList);

        const pcRef = doc(db, "pickups", id);
        const pcSnap = await getDoc(pcRef);
        
        if (pcSnap.exists()) {
          setPcData({ id: pcSnap.id, ...pcSnap.data() });
        } else {
          alert("Pickup Challan not found");
          navigate('/ops/pickup-challan');
        }
      } catch (error) {
        console.error("Error fetching PC data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, navigate]);

  const handleExportPDF = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>PC ${pcData?.pcNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 0px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 12px; }
            @page { margin: 1cm; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .bg-grey { backgroundColor: #f2f2f2; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  if (loading) return <Spinner />;
  if (!pcData) return null;

  const selectedFirm = firms.find(f => f.id === pcData.firmId);

  const renderVal = (val) => (val === 0 || val === '0' || !val ? '' : val);

  const renderPreview = () => (
    <div style={pdfContainerStyle} ref={printRef}>
      {/* SECTION 1: HEADER */}
      <table style={headerTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center' }}>
              {selectedFirm?.logo && <img src={selectedFirm.logo} alt="Logo" style={{ maxWidth: '120px' }} />}
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', fontSize: '12px' }}>
              <strong style={{ fontSize: '16px' }}>{selectedFirm?.name}</strong><br />
              {selectedFirm?.address}<br />
              GSTIN: {selectedFirm?.gstin}<br />
              Email: {selectedFirm?.email} | Tel: {selectedFirm?.tel}
            </td>
            <td style={{ ...headerTableCellStyle, width: '30%', textAlign: 'center', verticalAlign: 'middle' }}>
              <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '900' }}>PICKUP CHALLAN</h1>
              <div style={{ fontSize: '12px', marginTop: '5px' }}>({pcData.pcType})</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 2: DOCUMENT & PICKUP DETAILS */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '25%' }}><strong>PC Number:</strong> {pcData.pcNo}</td>
            <td style={{ ...headerTableCellStyle, width: '75%' }} colSpan="3">
              <strong>Against DC#:</strong> {pcData.referenceNo} &nbsp;&nbsp;&nbsp; 
              <strong>DC Dated:</strong> {pcData.dcDate || 'N/A'}
            </td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '25%' }}><strong>PC Date:</strong> {pcData.createdAt?.toDate ? pcData.createdAt.toDate().toLocaleDateString('en-IN') : 'N/A'}</td>
            <td style={{ ...headerTableCellStyle, width: '25%' }}><strong>Intimation Date:</strong> {pcData.intimationDate || 'N/A'}</td>
            <td style={{ ...headerTableCellStyle, width: '25%' }}><strong>Pickup Agent:</strong> {pcData.pickupAgent}</td>
            <td style={{ ...headerTableCellStyle, width: '25%' }}><strong>Coordinator:</strong> {pcData.coordinator}</td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 3: BILL TO | SHIP TO */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Consignee (Billing)</td>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>Pickup Location (Site)</td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, height: '80px', verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>
              {pcData.consigneeDetails}
            </td>
            <td style={{ ...headerTableCellStyle, height: '80px', verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>
              {pcData.pickupLocation}
            </td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}><strong>GSTIN/UIN:</strong> {pcData.gstin}</td>
            <td style={headerTableCellStyle}><strong>Salesperson:</strong> {pcData.salesperson}</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}>
              <strong>Order By:</strong> {pcData.orderBy?.name} &nbsp; | &nbsp; 
              <strong>Contact:</strong> {pcData.orderBy?.contact} &nbsp; | &nbsp; 
              <strong>Email:</strong> {pcData.orderBy?.email}
            </td>
            <td style={headerTableCellStyle}>
              <strong>Site Person:</strong> {pcData.sitePerson?.name} &nbsp; | &nbsp; 
              <strong>Contact:</strong> {pcData.sitePerson?.contact} &nbsp; | &nbsp; 
              <strong>Email:</strong> {pcData.sitePerson?.email}
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION: REMARKS (Moved above components) */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={headerTableCellStyle}>
              <strong>Remarks:</strong> {pcData.remarks || 'No remarks provided.'}
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 4: MATERIAL TABLE */}
      <div>
          <table style={{ ...headerTableStyle, borderTop: 'none' }}>
              <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={pdfThStyle}>Sr.</th>
                    <th style={pdfThStyle}>SKU</th>
                    <th style={{...pdfThStyle, textAlign:'left'}}>Description</th>
                    <th style={pdfThStyle}>Total Qty</th>
                    <th style={pdfThStyle}>Good</th>
                    <th style={pdfThStyle}>Damage</th>
                    <th style={pdfThStyle}>Missing</th>
                    <th style={pdfThStyle}>Total Lifted</th>
                    <th style={pdfThStyle}>Active</th>
                  </tr>
              </thead>
              <tbody>
                  {pcData.components?.map((c, i) => (
                      <tr key={i}>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{i+1}</td>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{c.sku}</td>
                          <td style={pdfTdStyle}>{c.name || c.description}</td>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{renderVal(c.quantity)}</td>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{renderVal(c.liftedGood)}</td>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{renderVal(c.liftedDamage)}</td>
                          <td style={{...pdfTdStyle, textAlign:'center'}}>{renderVal(c.finalMissing)}</td>
                          <td style={{...pdfTdStyle, textAlign:'center', fontWeight:'bold'}}>{renderVal(c.totalLifted)}</td>
                          <td style={{...pdfTdStyle, textAlign:'center'}}></td>
                      </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>
                    <td colSpan="3" style={{ ...pdfTdStyle, textAlign: 'right', paddingRight: '10px' }}>TOTALS:</td>
                    <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{renderVal(pcData.components?.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0))}</td>
                    <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{renderVal(pcData.components?.reduce((acc, curr) => acc + (Number(curr.liftedGood) || 0), 0))}</td>
                    <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{renderVal(pcData.components?.reduce((acc, curr) => acc + (Number(curr.liftedDamage) || 0), 0))}</td>
                    <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{renderVal(pcData.components?.reduce((acc, curr) => acc + (Number(curr.finalMissing) || 0), 0))}</td>
                    <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{renderVal(pcData.components?.reduce((acc, curr) => acc + (Number(curr.totalLifted) || 0), 0))}</td>
                    <td style={{ ...pdfTdStyle, textAlign: 'center' }}></td>
                  </tr>
              </tbody>
          </table>
      </div>

      {/* SECTION 5: HANDOVER */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>
                <strong>Handed Over By:</strong> {pcData.handedOverBy?.name}<br/><br/>
                <strong>Contact Number:</strong> {pcData.handedOverBy?.contact}<br/><br/>
                <strong>Date & Time:</strong> {pcData.handedOverBy?.dateTime?.replace('T', ' ')}
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', textAlign: 'center', verticalAlign: 'bottom', height: '100px' }}>
                <strong>Customer Seal and Signature</strong>
            </td>
          </tr>
        </tbody>
      </table>
      
      {/* SECTION 6: TERMS & CONDITIONS */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}><td style={{ ...headerTableCellStyle, fontSize: '14px' }}>Terms and Conditions</td></tr>
          <tr>
            <td style={{ ...headerTableCellStyle, fontSize: '10px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                {pcData.terms}
            </td>
          </tr>
        </tbody>
      </table>

      {/* SECTION 7: INTERNAL PURPOSE ONLY */}
      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold' }}>
            <td colSpan="3" style={{ ...headerTableCellStyle, textAlign: 'center' }}>Internal Purpose Only (Warehouse Receipt)</td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '33%', height: '100px', verticalAlign: 'top' }}>
              <strong>Received at warehouse by:</strong><br/>
              {pcData.receivedBy || '____________________'}<br/><br/>
              <strong>Time & Date:</strong><br/>
              {pcData.receivedAt?.replace('T', ' ') || '____________________'}
            </td>
            <td style={{ ...headerTableCellStyle, width: '34%', height: '100px', verticalAlign: 'top' }}>
              <strong>Verified and Checked By:</strong><br/>
              {pcData.verifiedBy || '____________________'}<br/><br/>
              <strong>Sign:</strong><br/>
              ____________________
            </td>
            <td style={{ ...headerTableCellStyle, width: '33%', textAlign: 'center', verticalAlign: 'bottom', paddingBottom: '15px' }}>
                <strong>Seal & Sign (Warehouse)</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/pickup-challan')} style={backBtnStyle}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0 }}>PC Preview: {pcData.pcNo}</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate(`/ops/pickup-challan/edit/${id}`)} style={editBtnStyle}>
            <Edit2 size={18} /> Edit PC
          </button>
          <button onClick={handleExportPDF} style={secondaryBtnStyle}>
            <Printer size={18} /> Export PDF
          </button>
        </div>
      </div>

      <div style={statusBannerStyle(pcData.status)}>
        {pcData.status === 'Confirmed' ? <CheckCircle size={20} /> : <Clock size={20} />}
        <span>Pickup Challan is currently in <strong>{pcData.status}</strong> status.</span>
      </div>

      {renderPreview()}
    </div>
  );
};

const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', minHeight: '1000px', color: '#000', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', borderRadius: '8px' };
const headerTableStyle = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000' };
const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '12px', textAlign: 'left' };
const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '11px', textAlign: 'center', fontWeight: 'bold' };
const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '11px', textAlign: 'left' };
const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', backgroundColor: 'white', padding: '15px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const editBtnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none', backgroundColor: '#2563eb', color: 'white' };
const secondaryBtnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151' };

const statusBannerStyle = (status) => ({
  backgroundColor: status === 'Confirmed' ? '#f0fdf4' : '#f8fafc',
  color: status === 'Confirmed' ? '#166534' : '#475569',
  padding: '12px 20px',
  borderRadius: '8px',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  fontSize: '14px',
  border: `1px solid ${status === 'Confirmed' ? '#bbf7d0' : '#e2e8f0'}`
});

export default PCDetail;